import { useState, useMemo, useCallback } from "react";
import VerbamailIcon from "../../../../../../../../../../../assets/svg/verbamail-icon";
import ChevronDownIcon from "../../../../../../../../../../../assets/svg/chevron-down-icon";
import ReplyIcon from "../../../../../../../../../../../assets/svg/reply-icon";
import { CommentAuthor } from "./CommentAuthor";
import { type TicketMail, type TicketCommentCreatedBy } from "./CommentCard";
import { type AttachmentPreviewItem } from "../../../../../../../../../../Communications/utils/types";
import { type AttachmentItem } from "../../../../../../../../../../../components/Common/AttachmentInput";
import AttachmentCarousel from "../../../../../../../../../../../components/Common/AttachmentCarousel";
import AttachmentPreviewModal from "../../../../../../../../../../Communications/components/CommunicationDetailsPanel/components/AttachmentPreviewModal";
import { getDateTimeByFormat } from "../../../../../../../../../../../redux/slices/dateTimeFormatSlice";
import { processHtmlContent } from "../../../../../../../../../../../utils/functions/formatters";
import { isHTML } from "../../../../../../../../../../Communications/utils/functions";
import { useEventChangeOutside } from "../../../../../../../../../../../hooks/useEventChangeOutside";
import EmailBodyIframe from "./EmailBodyIframe";

const resolveCreatedByName = (
  createdBy: TicketCommentCreatedBy | undefined
): string | undefined => {
  if (typeof createdBy === "object") return createdBy?.name ?? "Unknown";
  return "Unknown";
};

const getUniqueRepliers = (replies: TicketMail[]) => {
  const seen = new Set<string>();
  return replies?.reduce<TicketCommentCreatedBy[]>((acc, reply) => {
    const name = resolveCreatedByName(reply?.created_by) ?? "";
    if (!seen?.has(name)) {
      seen?.add(name);
      acc?.push(reply?.created_by);
    }
    return acc;
  }, []);
};

interface ActivityEmailItemProps {
  mail: TicketMail;
  variant?: "full" | "compact";
  containerClassName?: string;
  onReply?: () => void;
  onShowThread?: () => void;
}

const COLLAPSED_MAX_HEIGHT = 68;

const ActivityEmailItem = ({
  mail,
  variant = "full",
  containerClassName = "p-3",
  onReply,
  onShowThread,
}: ActivityEmailItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isEmailDetailsOpen, setIsEmailDetailsOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);

  const emailDetailsRef = useEventChangeOutside<HTMLDivElement>(() => {
    setIsEmailDetailsOpen(false);
  }, "mousedown");

  const processedContent = useMemo(() => {
    if (!mail?.content) return "";
    if (isHTML(mail.content)) {
      return processHtmlContent(mail.content);
    }
    return mail.content;
  }, [mail?.content]);

  const attachmentsWithTimestamp: AttachmentPreviewItem[] = useMemo(() => {
    return (mail?.attachments ?? []).map((att) => ({
      s3_key: att.s3_key,
      s3_url: att.s3_url,
      file_name: att.file_name,
      content_type: att.content_type,
      timestamp: mail.created_at,
    }));
  }, [mail?.attachments, mail?.created_at]);

  const carouselItems: AttachmentItem[] = useMemo(() => {
    if (!mail?.attachments || mail.attachments.length === 0) return [];
    return mail.attachments.map((att, index) => ({
      id: att.s3_key || String(index),
      file: null,
      name: att.file_name,
      mime: att.content_type,
      previewUrl: att.s3_url,
      status: "sent" as const,
    }));
  }, [mail?.attachments]);

  const handleOverflowChange = useCallback((overflowing: boolean) => {
    setIsOverflowing(overflowing);
  }, []);

  const handleCarouselPreview = useCallback(
    (_item: AttachmentItem, index?: number) => {
      setPreviewIndex(index ?? 0);
      setIsPreviewModalOpen(true);
    },
    []
  );

  const replyCount = mail?.replies?.length ?? 0;
  const firstRecipient = mail?.mail_data?.to?.[0];
  const recipientDisplay = firstRecipient
    ? firstRecipient.split("@")[0]
    : undefined;

  return (
    <div className="border-b border-border-gray dark:border-border-gray-dk">
      <div className="flex flex-col gap-3">
        <div
          className={`bg-card-yellow-bg dark:bg-card-yellow-bg-dk ${containerClassName} flex flex-col gap-2`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 py-[3px]">
              <VerbamailIcon className="w-[18px] h-[18px] text-verba-mail dark:text-verba-mail-dk" />
              <p className="font-medium text-md text-heading-text dark:text-heading-text-dk">
                VerbaMail
              </p>
            </div>

            {recipientDisplay && (
              <div ref={emailDetailsRef} className="relative">
                <button
                  className="flex items-center gap-1 py-[3px] cursor-pointer"
                  onClick={() => setIsEmailDetailsOpen(!isEmailDetailsOpen)}
                >
                  <p className="text-dark-gray dark:text-dark-gray-dk text-md font-normal">
                    to {recipientDisplay}
                  </p>
                  <ChevronDownIcon
                    className={`w-[10px] h-[10px] text-verba-indigo dark:text-verba-indigo-dk transition-transform ${
                      isEmailDetailsOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {isEmailDetailsOpen && (
                  <div className="flex flex-col gap-[6px] min-w-[260px] px-3 py-3 border border-border-gray dark:border-border-gray-dk rounded-lg shadow-popper-blur-effect backdrop-blur-2xl absolute top-7 right-0 bg-tooltip-bg dark:bg-tooltip-bg-dk z-10">
                    <div className="flex items-start gap-2">
                      <p className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk font-normal shrink-0">
                        From:
                      </p>
                      <p className="text-pure-white dark:text-pure-white-dk text-md font-normal break-all">
                        {mail.mail_data.from_email}
                      </p>
                    </div>
                    {mail.mail_data.to?.length > 0 && (
                      <div className="flex items-start gap-2">
                        <p className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk font-normal shrink-0">
                          To:
                        </p>
                        <div className="flex flex-col gap-0.5 text-pure-white dark:text-pure-white-dk text-md font-normal">
                          {mail.mail_data.to.map((email) => (
                            <span key={email}>{email}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {mail.mail_data.cc?.length > 0 && (
                      <div className="flex items-start gap-2">
                        <p className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk font-normal shrink-0">
                          Cc:
                        </p>
                        <div className="flex flex-col gap-0.5 text-pure-white dark:text-pure-white-dk text-md font-normal">
                          {mail.mail_data.cc.map((email) => (
                            <span key={email}>{email}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {mail.mail_data.bcc?.length > 0 && (
                      <div className="flex items-start gap-2">
                        <p className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk font-normal shrink-0">
                          Bcc:
                        </p>
                        <div className="flex flex-col gap-0.5 text-pure-white dark:text-pure-white-dk text-md font-normal">
                          {mail.mail_data.bcc.map((email) => (
                            <span key={email}>{email}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {mail.mail_data.subject && (
                      <div className="flex items-start gap-2">
                        <p className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk font-normal shrink-0">
                          Subject:
                        </p>
                        <p className="text-pure-white dark:text-pure-white-dk text-md font-normal">
                          {mail.mail_data.subject}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2.5">
            {mail.mail_data?.subject && (
              <div className="flex flex-col gap-0.5">
                <span className="text-xxs uppercase font-medium text-gray-placeholder dark:text-gray-placeholder-dk leading-[1.3]">
                  Subject
                </span>
                <span className="text-sm font-normal text-heading-text dark:text-heading-text-dk leading-normal">
                  {mail.mail_data.subject}
                </span>
              </div>
            )}

            {processedContent && (
              <div className="flex flex-col gap-0.5">
                <span className="text-xxs uppercase font-medium text-gray-placeholder dark:text-gray-placeholder-dk leading-[1.3]">
                  Body
                </span>
                <div className="relative overflow-x-auto ">
                  <EmailBodyIframe
                    html={processedContent}
                    maxCollapsedHeight={COLLAPSED_MAX_HEIGHT}
                    isExpanded={isExpanded}
                    onOverflowChange={handleOverflowChange}
                  />
                </div>
              </div>
            )}
            {isOverflowing && (
              <p
                className=" cursor-pointer"
                onClick={() => setIsExpanded((prev) => !prev)}
              >
                <span className="text-md text-verba-blue dark:text-verba-blue-dk font-medium hover:underline">
                  {isExpanded ? "read less" : "...read more"}
                </span>
              </p>
            )}
            {carouselItems.length > 0 && (
              <div className="flex flex-col gap-0.5">
                <span className="text-xxs uppercase font-medium text-gray-placeholder dark:text-gray-placeholder-dk leading-[1.3]">
                  Attachments
                </span>
                <AttachmentCarousel
                  items={carouselItems}
                  onPreview={handleCarouselPreview}
                  onRemove={() => {}}
                  hideDeleteButton={true}
                  isSent={true}
                />
              </div>
            )}
          </div>
        </div>

        {onShowThread && replyCount > 0 && (
          <button
            className="flex items-center justify-center gap-1 w-full bg-hover-effect dark:bg-hover-effect-dk  px-2 py-1 cursor-pointer"
            onClick={onShowThread}
          >
            <ReplyIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk shrink-0" />
            <p className="text-xs font-medium text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap">
              Show Email Thread
            </p>
            <span className="text-xs font-medium text-analytics-gray dark:text-analytics-gray-dk">
              &middot;
            </span>
            <p className="text-xs font-medium text-analytics-gray dark:text-analytics-gray-dk whitespace-nowrap">
              {replyCount} {replyCount === 1 ? "Email" : "Emails"}
            </p>
            {replyCount > 0 && (
              <div className="flex items-center pr-[6px] shrink-0">
                {getUniqueRepliers(mail.replies)
                  .slice(0, 3)
                  .map((createdBy, index) => (
                    <div
                      key={resolveCreatedByName(createdBy) ?? index}
                      className="relative shrink-0"
                      style={{
                        marginLeft: index > 0 ? "-6px" : 0,
                        zIndex: 3 - index,
                      }}
                    >
                      <CommentAuthor
                        userName={resolveCreatedByName(createdBy)}
                        showName={false}
                      />
                    </div>
                  ))}
              </div>
            )}
          </button>
        )}

        <div className={`${containerClassName} pt-0 flex flex-col gap-3`}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-[5px] flex-1 min-w-0">
              <CommentAuthor
                userName={resolveCreatedByName(mail?.created_by)}
              />
            </div>
            <div className="flex items-center gap-[5px] shrink-0">
              <p className="text-xs font-medium text-gray-placeholder dark:text-gray-placeholder-dk whitespace-nowrap">
                {getDateTimeByFormat(new Date(mail?.created_at))?.date},{" "}
                {getDateTimeByFormat(new Date(mail?.created_at))?.time}
              </p>
              {onReply && (
                <>
                  <span className="text-border-gray dark:text-border-gray-dk">
                    |
                  </span>
                  <button
                    className="flex items-center gap-0.5 cursor-pointer hover:opacity-80"
                    onClick={onReply}
                  >
                    <ReplyIcon className="w-[14px] h-[14px] text-verba-indigo dark:text-verba-indigo-dk" />
                    <span className="text-xs font-medium text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap">
                      Reply
                    </span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {attachmentsWithTimestamp.length > 0 && (
        <AttachmentPreviewModal
          open={isPreviewModalOpen}
          onOpenChange={setIsPreviewModalOpen}
          attachments={attachmentsWithTimestamp}
          initialIndex={previewIndex}
        />
      )}
    </div>
  );
};

export default ActivityEmailItem;
